import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer } from "lucide-react";
import { LUCIDA, senderLine, longDate, LetterHead, LetterFooter, stripLeadingName } from "@/components/Letterhead";

export default function DeliveryNote() {
  const { id } = useParams();
  const nav = useNavigate();
  const [dn, setDn] = useState(null);
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get(`/delivery-notes/${id}`).then((r) => setDn(r.data)).catch(() => {}); }, [id]);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  if (!dn) return <div className="text-muted-foreground p-8">Lädt…</div>;

  const company = settings?.company || dn.seller || {};
  const logo = dn.seller?.logo_data_url || company.logo_data_url;
  const contact = (dn.ship_to?.contact || "").trim();
  const firstName = contact ? contact.split(/\s+/)[0] : "";
  // Anrede wird beim Anlegen des Belegs in der Belegsprache eingefroren (Backend). Der Fallback
  // greift nur für Altbelege ohne Snapshot.
  const salutation = dn.salutation || (firstName ? `Hallo ${firstName},` : "Guten Tag,");

  return (
    <div>
      <div className="flex items-center justify-between mb-4 print:hidden">
        <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="dn-back"><ArrowLeft className="h-4 w-4" /> Zurück</button>
        <Button onClick={() => window.print()} data-testid="dn-print"><Printer className="h-4 w-4 mr-1.5" /> Drucken / PDF</Button>
      </div>

      <div className="mx-auto a4-page bg-white text-black" style={LUCIDA} data-testid="dn-doc">
        <LetterHead logo={logo} company={company} />

        <div className="mt-12">
          <div className="text-[10px] text-neutral-500 underline underline-offset-2">{senderLine(company)}</div>
          <div className="mt-3 text-sm leading-6">
            <div className="font-semibold">{dn.ship_to?.name}</div>
            <div className="whitespace-pre-line">{stripLeadingName(dn.ship_to?.address, dn.ship_to?.name)}</div>
          </div>
        </div>

        <div className="mt-10 flex justify-between items-baseline gap-4">
          <div className="text-lg font-bold" data-testid="dn-title">Lieferschein: {dn.delivery_note_number}</div>
          <div className="text-sm text-neutral-700">{longDate(dn.issued_at)}</div>
        </div>

        <div className="mt-6 text-sm" data-testid="dn-salutation">{salutation}</div>
        <div className="mt-2 text-sm">anbei der Lieferschein gemäß Ihrer Beauftragung.</div>

        <table className="w-full text-sm mt-6 border-t border-neutral-400">
          <thead>
            <tr className="text-left font-bold border-b border-neutral-300">
              <th className="py-2 pr-4 text-right w-12">Pos.</th>
              <th className="py-2 pr-4 text-right w-16">Menge</th>
              <th className="py-2 pr-4">Leistungsbeschreibung</th>
              <th className="py-2">EAN</th>
            </tr>
          </thead>
          <tbody>
            {dn.lines.map((l, i) => (
              <tr key={i} className="border-b border-neutral-200 align-top">
                <td className="py-2 pr-4 text-right">{i + 1}</td>
                <td className="py-2 pr-4 text-right">{l.quantity}</td>
                <td className="py-2 pr-4">{l.name}{l.is_bundle && <span className="text-xs text-neutral-500"> (Bundle)</span>}{l.quality === "second_choice" && <span className="ml-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 align-middle">2. WAHL</span>}</td>
                <td className="py-2">{l.ean || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-8 text-sm">Die Rechnung kommt als separates Dokument.</div>
        <div className="mt-6 text-sm">Herzlichen Dank</div>
        <div className="mt-1 text-base font-bold">Gluty-Free Club</div>
        <div className="text-xs italic text-neutral-500">powered by Consberry GmbH</div>

        <LetterFooter company={company} extra={dn.lucid_id ? `LUCID-Nr. (VerpackG): ${dn.lucid_id}` : null} />
      </div>
    </div>
  );
}
